"use client"

import { useState, useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { format } from "date-fns"
import { formatDateToUTC } from "@/lib/utils/date"
import { HelpCircle, X } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogPortal, DialogOverlay } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { motion, AnimatePresence } from "framer-motion"
import { useToast } from "@/hooks/use-toast"
import { useAuth } from "@/hooks/use-auth"
import { useChildStore } from "@/lib/store/child"
import { supabase } from "@/integrations/supabase/client"
import { useCaregiverStore } from "@/lib/store/caregiver"
import { useQueryClient } from "@tanstack/react-query"
import { NapFormProvider, napFormSchema, type NapFormData } from "./nap/NapFormContext"
import { TimeStep } from "./nap/steps/TimeStep"
import { LocationStep } from "./nap/steps/LocationStep"
import { EnvironmentStep } from "./nap/steps/EnvironmentStep"
import { OnsetStep } from "./nap/steps/OnsetStep"
import { SleepLatencyStep } from "./nap/steps/SleepLatencyStep"
import { RestfulnessStep } from "./nap/steps/RestfulnessStep"
import { SleepDebtStep } from "./nap/steps/SleepDebtStep"
import { NotesStep } from "./nap/steps/NotesStep"
import { NapStepContainer, NAP_STEPS, NapStepType } from "./nap/NapStepContainer"
import { Database } from "@/types/supabase"
import { fetchLookupData, LookupItem, LookupTables } from "@/types/activity"

// Enhanced type for activity editing that includes UI-specific fields
interface NapActivity extends Partial<Database["public"]["Tables"]["nap_sessions"]["Row"]> {
  id: string;
  childId?: string;
  startTime: string;
  endTime?: string;
  location?: string;
  locationName?: string;
  environment?: string;
  environmentName?: string;
  onset_method?: string;
  onsetMethodName?: string;
  quality?: string | number;
  qualityName?: string;
  notes?: string;
  sleep_latency?: number;
  sleep_debt_signs?: boolean | null;
  type?: string;
}

interface NapDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  childId?: string
  activityToEdit?: NapActivity
  onSuccess?: (data: Database["public"]["Tables"]["nap_sessions"]["Row"]) => void
}

export function NapDialog({ open, onOpenChange, childId, activityToEdit, onSuccess }: NapDialogProps) { 
  const { toast } = useToast()
  const { user } = useAuth()
  const { caregiverId } = useCaregiverStore()
  const { selectedChild } = useChildStore()
  const [saving, setSaving] = useState(false)
  const [currentStep, setCurrentStep] = useState<NapStepType>('start')
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [locations, setLocations] = useState<LookupItem[]>([])
  const [environments, setEnvironments] = useState<LookupItem[]>([])
  const [onsetMethods, setOnsetMethods] = useState<LookupItem[]>([])
  const [restfulnessRatings, setRestfulnessRatings] = useState<LookupItem[]>([])
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)

  // Determine the effective child ID to use, prioritizing the explicitly passed childId
  const effectiveChildId = useMemo(() => {
    const id = childId || selectedChild?.id || '';
    
    console.log('NapDialog: determining effective child ID', {
      providedChildId: childId,
      storeSelectedChildId: selectedChild?.id,
      effectiveChildId: id
    });
    
    return id;
  }, [childId, selectedChild?.id]);
  
  // Current selected child object with fallback
  const currentChild = useMemo(() => {
    if (childId && selectedChild?.id !== childId) {
      // Try to find the child in the store by ID
      const foundChild = selectedChild?.id === childId ? selectedChild : null;
      return foundChild || { id: childId, name: 'Selected Child' }; // Fallback with placeholder
    }
    return selectedChild;
  }, [childId, selectedChild]);

  // Get the effective caregiver ID (either from the activity being edited or the current user)
  const effectiveCaregiverId = useMemo(() => {
    if (isEditing && activityToEdit?.caregiver_id) {
      return activityToEdit.caregiver_id;
    }
    return caregiverId;
  }, [isEditing, activityToEdit, caregiverId]);

  const currentStepIndex = useMemo(() => 
    NAP_STEPS.findIndex(step => step === currentStep)
  , [currentStep])

  // Set whether we're editing on mount
  useEffect(() => {
    if (activityToEdit) {
      console.log('😴 NAP DIALOG - EDITING MODE', {
        id: activityToEdit.id,
        type: activityToEdit.type,
        startTime: activityToEdit.startTime,
        endTime: activityToEdit.endTime,
        location: activityToEdit.location,
        environment: activityToEdit.environment,
        onset_method: activityToEdit.onset_method,
        quality: activityToEdit.quality,
        notes: activityToEdit.notes
      });
      setIsEditing(true)
    } else {
      console.log('😴 NAP DIALOG - CREATE MODE');
      setIsEditing(false)
    }
  }, [activityToEdit])

  // Initialize the form
  const form = useForm<NapFormData>({
    resolver: zodResolver(napFormSchema),
    defaultValues: useMemo(() => {
      if (activityToEdit) {
        setIsEditing(true)
        const startTime = activityToEdit.startTime ? new Date(activityToEdit.startTime) : new Date()
        const endTime = activityToEdit.endTime ? new Date(activityToEdit.endTime) : new Date()
        
        return {
          childId: activityToEdit.childId || effectiveChildId,
          caregiverId: effectiveCaregiverId,
          start_time: startTime,
          start_time_input: format(startTime, 'HH:mm'),
          end_time: endTime,
          end_time_input: format(endTime, 'HH:mm'),
          location_id: activityToEdit.location || '',
          environment_id: activityToEdit.environment || '',
          onset_method_id: activityToEdit.onset_method || '',
          restfulness_id: activityToEdit.quality ? String(activityToEdit.quality) : '',
          notes: activityToEdit.notes || '',
          signs_of_sleep_debt: activityToEdit.sleep_debt_signs || false,
          sleep_latency: activityToEdit.sleep_latency || 0
        }
      } else {
        return {
          childId: effectiveChildId,
          caregiverId: caregiverId,
          start_time: new Date(),
          start_time_input: format(new Date(), 'HH:mm'),
          end_time: new Date(Date.now() + 30 * 60000),
          end_time_input: format(new Date(Date.now() + 30 * 60000), 'HH:mm'),
          location_id: '',
          environment_id: '',
          onset_method_id: '',
          restfulness_id: '',
          notes: '',
          signs_of_sleep_debt: false,
          sleep_latency: 0
        }
      }
    }, [activityToEdit, effectiveChildId, effectiveCaregiverId, caregiverId])
  })

  // Reset form when dialog opens or activityToEdit changes
  useEffect(() => {
    if (open) {
      form.reset(form.getValues())
      setCurrentStep('start')
      setShowConfirmation(false)
    }
  }, [open, activityToEdit])

  useEffect(() => {
    async function loadLookupData() {
      try {
        const lookupData = await fetchLookupData();
        
        setLocations(lookupData.locations);
        setEnvironments(lookupData.environments);
        setOnsetMethods(lookupData.onsetMethods);
        setRestfulnessRatings(lookupData.restfulnessRatings);
      } catch (err) {
        console.error('Error loading lookup data:', err)
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load nap tracking options"
        })
      }
    }

    if (open) {
      loadLookupData()
    }
  }, [open, toast])

  const handleClose = () => {
    onOpenChange(false)
    form.reset()
    setCurrentStep('start')
    setShowConfirmation(false)
  }

  const nextStep = () => {
    if (window.navigator && window.navigator.vibrate) {
      window.navigator.vibrate(50)
    }
    
    const nextIndex = currentStepIndex + 1
    if (nextIndex < NAP_STEPS.length) {
      setCurrentStep(NAP_STEPS[nextIndex])
    }
  }

  const prevStep = () => {
    if (window.navigator && window.navigator.vibrate) {
      window.navigator.vibrate(50)
    }

    const prevIndex = currentStepIndex - 1
    if (prevIndex >= 0) {
      setCurrentStep(NAP_STEPS[prevIndex])
    }
  }

  // Function to save the nap session
  const onSubmit = async (values: NapFormData) => {
    try {
      console.log('Starting nap session submission...')
      setSaving(true)

      if (!effectiveChildId) {
        console.error('No child ID found:', { childId: effectiveChildId })
        toast({
          variant: "destructive",
          title: "Error",
          description: "No child selected. Please select a child to log a nap for.",
        })
        setSaving(false)
        return
      }

      if (!effectiveCaregiverId) {
        console.error('No caregiver ID found:', { caregiverId: effectiveCaregiverId })
        toast({
          variant: "destructive",
          title: "Error",
          description: "Caregiver information is missing. Please refresh and try again.",
        })
        setSaving(false)
        return
      }
      
      // Parse dates and times
      const startDate = new Date(values.start_time as Date)
      const endDate = new Date(values.end_time as Date)
      
      console.log('Initial dates:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        startTimeInput: values.start_time_input,
        endTimeInput: values.end_time_input
      })

      const [startHours, startMinutes] = (values.start_time_input || '00:00').split(':').map(Number)
      const [endHours, endMinutes] = (values.end_time_input || '00:00').split(':').map(Number)
      
      console.log('Parsed times:', {
        startHours,
        startMinutes,
        endHours,
        endMinutes
      })
      
      startDate.setHours(startHours, startMinutes)
      endDate.setHours(endHours, endMinutes)
      
      // Ensure end time is after start time
      if (endDate < startDate) {
        endDate.setDate(endDate.getDate() + 1)
      }
      
      // Get user's timezone for better debugging
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const tzOffset = new Date().getTimezoneOffset();
      
      console.log('[NapDialog.tsx:270] TIMEZONE DEBUGGING - Before formatDateToUTC:');
      console.log(`  User timezone: ${userTimeZone}`);
      console.log(`  Timezone offset: ${tzOffset} minutes (${tzOffset/60} hours)`);
      console.log(`  Start date (local): ${startDate.toLocaleString()}`);
      console.log(`  End date (local): ${endDate.toLocaleString()}`);
      
      // Convert to UTC for database storage
      const startTimeUTC = formatDateToUTC(startDate);
      const endTimeUTC = formatDateToUTC(endDate);
      
      console.log('[NapDialog.tsx:279] TIMEZONE DEBUGGING - After formatDateToUTC:');
      console.log(`  Start time UTC: ${startTimeUTC}`);
      console.log(`  End time UTC: ${endTimeUTC}`);
      console.log(`  Expected local -> UTC conversion:`,
        `${startDate.toLocaleString()} -> should be ~${tzOffset/60} hours different in UTC}`);
      
      // Log child ID details for debugging
      console.log('[NapDialog.tsx:288] CHILD ID DEBUGGING:');
      console.log(`  values.childId: ${values.childId}`);
      console.log(`  prop childId: ${childId}`);
      console.log(`  selectedChild?.id: ${selectedChild?.id}`);
      
      // Use the child ID from props or from the store
      if (!effectiveChildId) {
        console.error('[NapDialog.tsx:297] ERROR: No child ID available!');
        throw new Error('Child ID is required but not available');
      }

      console.log(`Using child ID: ${effectiveChildId}`);
      
      // Create the record data using the proper type
      const napRecord: Database["public"]["Tables"]["nap_sessions"]["Insert"] = {
        child_id: effectiveChildId,
        caregiver_id: effectiveCaregiverId,
        start_time: startTimeUTC,
        end_time: endTimeUTC,
        location_id: values.location_id || null,
        environment_id: values.environment_id || null,
        onset_method_id: values.onset_method_id || null,
        sleep_latency: values.sleep_latency || 0,
        restfulness_id: values.restfulness_id || null,
        signs_of_sleep_debt: values.signs_of_sleep_debt || null,
        notes: values.notes || null
      }
      
      let result: Database["public"]["Tables"]["nap_sessions"]["Row"];
      
      if (isEditing && activityToEdit?.id) {
        try {
          console.log('✏️ Updating nap session', { id: activityToEdit.id });
          
          const updateRecord: Database["public"]["Tables"]["nap_sessions"]["Update"] = {
            location_id: values.location_id || null,
            environment_id: values.environment_id || null,
            onset_method_id: values.onset_method_id || null,
            restfulness_id: values.restfulness_id || null,
            sleep_latency: values.sleep_latency || null,
            notes: values.notes?.trim() || '',
            start_time: startTimeUTC,
            end_time: endTimeUTC,
            caregiver_id: effectiveCaregiverId,
            child_id: effectiveChildId
          }
          
          console.log('✏️ Updating nap session:', { 
            id: activityToEdit.id,
            updateData: updateRecord,
            originalCaregiverId: activityToEdit.caregiver_id,
            effectiveCaregiverId,
            effectiveChildId
          });
          
          // Update existing record
          const { data, error } = await supabase
            .from('nap_sessions')
            .update(updateRecord)
            .eq('id', activityToEdit.id)
            .select()

          if (error) {
            console.error('❌ Error updating nap session:', error);
            throw error;
          }
          
          console.log('✅ Nap session updated successfully:', {
            id: activityToEdit.id,
            data: data && data.length > 0 ? data[0] : null
          });
          
          // If we got data back, use it; otherwise create a result with the ID
          if (data && data.length > 0) {
            result = data[0] as Database["public"]["Tables"]["nap_sessions"]["Row"];
          } else {
            result = { id: activityToEdit.id, ...updateRecord } as Database["public"]["Tables"]["nap_sessions"]["Row"];
          }
          
          // Show success message
          toast({
            title: "Success",
            description: "Nap session updated successfully",
            duration: 2000
          });
          
          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ['napSessions'] });
        } catch (error) {
          console.error('❌ Error in nap session update:', error);
          toast({
            variant: "destructive",
            title: "Error",
            description: error instanceof Error ? error.message : "Failed to update nap session"
          });
          throw error;
        }
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('nap_sessions')
          .insert(napRecord)
          .select()

        if (error) throw error
        if (!data || data.length === 0) {
          throw new Error('No data returned from insert operation');
        }
        result = data[0] as Database["public"]["Tables"]["nap_sessions"]["Row"];
      }

      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['napRecords'] });
      
      // Reset form, close dialog, and show success message
      toast({
        title: isEditing ? "Nap updated" : "Nap logged",
        description: "Your baby's nap details have been saved",
        duration: 2000
      })

      // Trigger callback if provided
      if (onSuccess) {
        onSuccess(result)
      }

      // Reset and close
      form.reset()
      handleClose()
    } catch (err) {
      console.error('Error saving nap record:', err)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save nap record. Please try again."
      })
    } finally {
      setSaving(false)
    }
  }

  // Render the current step based on state
  const renderCurrentStep = () => {
    if (showConfirmation) {
      return (
        <div className="bg-card rounded-lg shadow-lg p-6 border border-border">
          <h3 className="text-lg font-semibold mb-4 text-white">
            {isEditing ? "Save Changes?" : "Save Nap Record?"}
          </h3>
          <p className="mb-6 text-gray-300">
            {isEditing 
              ? "You're about to update this nap record. Please review your changes before confirming."
              : "You're about to save this nap record. Please review the details before confirming."
            }
          </p>
          <div className="flex gap-3 justify-end">
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmation(false)}
              type="button"
              className="border-white/10 text-gray-300 hover:bg-white/5"
            >
              Back to Edit
            </Button>
            <Button 
              variant="default"
              onClick={() => {
                form.setValue('confirmed' as any, true)
                form.handleSubmit(onSubmit)()
              }}
              type="button"
              disabled={saving}
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              {saving 
                ? isEditing ? "Updating..." : "Saving..." 
                : isEditing ? "Confirm Changes" : "Confirm and Save"
              }
            </Button>
          </div>
        </div>
      );
    }

    // Render the appropriate step component based on current step
    switch (currentStep) {
      case 'start':
        return <TimeStep type="start" onNext={nextStep} />;
      case 'end':
        return <TimeStep type="end" onNext={nextStep} onPrev={prevStep} />;
      case 'location':
        return <LocationStep locations={locations} onNext={nextStep} onPrev={prevStep} />;
      case 'environment':
        return <EnvironmentStep environments={environments} onNext={nextStep} onPrev={prevStep} />;
      case 'onset':
        return <OnsetStep onsetMethods={onsetMethods} onNext={nextStep} onPrev={prevStep} />;
      case 'sleep_latency':
        return <SleepLatencyStep onNext={nextStep} onPrev={prevStep} />;
      case 'restfulness':
        return <RestfulnessStep restfulnessRatings={restfulnessRatings} onNext={nextStep} onPrev={prevStep} />;
      case 'sleep_debt':
        return <SleepDebtStep onNext={nextStep} onPrev={prevStep} />;
      case 'notes':
        return (
          <NotesStep 
            onNext={() => form.handleSubmit(onSubmit)()} 
            onPrev={prevStep}
            isSubmitting={saving}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogPortal>
        <DialogOverlay className="bg-black/85 backdrop-blur-sm" />
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-gradient-to-b from-slate-800 to-slate-900 border-2 border-slate-600 shadow-2xl z-[100] scale-100 rounded-xl">
          <DialogHeader className="border-b border-slate-700/50 pb-4">
            <DialogTitle className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
              {isEditing ? "Edit Nap Record" : "Record Nap"}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pt-4">
            <NapStepContainer
              form={form}
              currentStep={currentStep}
              onNext={nextStep}
              onPrev={prevStep}
            >
              {renderCurrentStep()}
            </NapStepContainer>
          </form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
